import {Avatar, Button, createStyles, IconButton, makeStyles, Menu, MenuItem, Theme, useMediaQuery} from '@material-ui/core';
import {AlertPayload, getUserAlerts} from 'Core/ducks/alerts';
import {ModalState, showModal} from 'Core/ducks/modal';
import {isLoaded, toPageEither} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import {sequenceT} from 'fp-ts/lib/Apply';
import {fold as foldEither} from 'fp-ts/lib/Either';
import {constant} from 'fp-ts/lib/function';
import {IO, io} from 'fp-ts/lib/IO';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {ReactNode, useEffect, useState} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {ReducerState} from 'rootReducer';
import MenuStateProvider from 'Shared/components/MenuStateProvider';
import RequireScope from 'Shared/components/RequireScope';
import withUserClaims, {WithClaimsProps} from 'Shared/components/withUserClaims';
import {Scope} from 'Shared/constants/Scope';
import Badge from 'Shared/utilities/Badge';
import {NoOp} from 'Shared/utilities/IOUtilities';
import {prop} from 'Shared/utilities/prop';
import * as AgencyAdministrationActions from 'Users/ducks/agentAdministration';
import {setSelectedAgency} from 'Users/ducks/userProfile';
import {getFullName} from 'Users/models/Agent';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';
import {UserTypeClaimKey, UserTypeClaims} from 'Users/models/Claims';
import {UserRouteConstants} from 'Users/Routes';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import './AccountMenu.sass';
import {bindActionCreators} from 'redux';
import {handleAlerts} from 'Core/components/Startup';
import * as CustomerSupportActions from 'Users/ducks/CustomerSupport';
import {toggleLoginSpinner} from '../../ducks/login';
import {logoutISAM} from '../../ducks/refreshToken';
import {changeAnalyticsState} from 'Core/ducks/analytics';
import {ANALYTICS_EVENT_ACTION} from '../Analytics/AnalyticsEventConstants';

export type AccountMenuProps = {
  showModal: typeof showModal;
  setSelectedAgency: typeof setSelectedAgency;
} & WithClaimsProps &
  typeof AgencyAdministrationActions &
  ReturnType<typeof mapStateToProps> &
  RouteComponentProps;

const renderAlertIcon = (onClick: IO<void>, alerts: Page<AlertPayload>) => (
  <IconButton className="alert-button" onClick={onClick} aria-label="Alerts">
    <Badge count={alerts.totalCount}>
      <i style={{color: '#004ea8'}} className="fa fa-bell" />
    </Badge>
  </IconButton>
);

const getAlertsCount = (alertsPage: AccountMenuProps['userAlerts']) =>
  pipe(alertsPage, toPageEither, foldEither(constant(0), prop('totalCount')));

const onShowAlerts = (alerts: Page<AlertPayload>, displayModal: typeof showModal) => () => {
  displayModal(
    ModalState.Alerts({
      messages: alerts.result,
      onDismissAlerts: () => {},
    })
  );
};
const AccountMenu = (props: AccountMenuProps) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const scopes = useSelector(selectUserScopes);
  const dispatch = useDispatch();
  const {userAlerts} = useSelector((state: ReducerState) => state.alerts);
  const {analytics} = useSelector((state: ReducerState) => state.analytics);
  const {modalState} = useSelector((state: ReducerState) => state.modal);
  const {showModal: boundShowModal} = bindActionCreators({showModal}, dispatch);
  const [translatey, setTranslatey] = useState('-45%');
  const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    custom: {
      transform: `translateX(${translatey}) translateY(0%) !important`,
      [theme.breakpoints.down('md')]: {
        transform: 'translateX(0%) !important',
      },
    }
  }));
  const classes = useStyles();

  useEffect(() => {
    dispatch(getUserAlerts());
  }, []);

  useEffect(() => {
    if (isLoaded(userAlerts) && modalState.tag === ModalState.None().tag) {
      handleAlerts(userAlerts, boundShowModal)();
    }
  }, [userAlerts]);

  const fullName = getFullName(props.claims);
  const AlertsIcon = () =>
    pipe(
      props.userAlerts,
      toPageEither,
      foldEither(
        () => null,
        alerts => renderAlertIcon(onShowAlerts(alerts, props.showModal), alerts)
      )
    );

  const getAlertItemLabel = () => {
    const alertsCount = getAlertsCount(props.userAlerts);
    return `Alerts (${alertsCount})`;
  };
  const getTaxId = (agency: AgentAgencyClaim) => `(${agency.maskedIdentifier.replace(/X/g, '')})`;

  return (
    <div className="account-menu">
      <AlertsIcon />
      <MenuStateProvider>
        {({close, open, anchor}) => (
          <>
            {isDesktop ? (
              <Button
                id="account-icon-button"
                color="inherit"
                className="account-menu__button"
                onClick={event => open(event.currentTarget)}
              >
                <span className="account-menu__user-name" data-dd-privacy="mask">
                  {fullName}
                </span>
                <span className="account-menu__tax-identifier">
                  {pipe(
                    props.selectedAgency,
                    O.fold(
                      () => '' as ReactNode,
                      agency => (
                        <>
                          <span data-dd-privacy="mask">{getTaxId(agency)}</span>
                          {
                            <span>
                              {agency.isNewYork ? (
                                <span>
                                  - <strong className="account-menu__new-york-symbol">NY</strong>
                                </span>
                              ) : null}
                            </span>
                          }
                        </>
                      )
                    )
                  )}
                </span>
                <i className="fa fa-angle-down" />
              </Button>
            ) : (
              <IconButton onClick={event => open(event.currentTarget)}>
                <Avatar className="account-menu__mobile-button">
                  {props.claims.firstName[0] + props.claims.lastName[0]}
                </Avatar>
              </IconButton>
            )}
            <Menu
              id="account-menu"
              className='account-menu__menu'
              getContentAnchorEl={null}
              anchorEl={anchor}
              classes={{
                paper: classes.custom
              }}
              anchorOrigin={{
                horizontal: 'right',
                vertical: 'bottom',
              }}
              transformOrigin={{
                horizontal: 'center',
                vertical: 'top',
              }}
              open={Boolean(anchor)}
              onClose={close}
            >
              <MenuItem
                disabled={getAlertsCount(props.userAlerts) === 0}
                className="account-menu__alerts-item account-menu__item"
                key="account-menu__alerts-item"
                onClick={pipe(
                  props.userAlerts,
                  toPageEither,
                  foldEither(
                    () => NoOp,
                    alerts => sequenceT(io)(onShowAlerts(alerts, props.showModal), close)
                  )
                )}
              >
                {getAlertItemLabel()}
              </MenuItem>
              <MenuItem
                key="account-menu__user-profile"
                className="account-menu__item"
                onClick={() => {
                  close();
                  props.history.push(UserRouteConstants.PROFILE);
                }}
              >
                User Profile
              </MenuItem>
              <RequireScope scopes={scopes} scope={Scope.SsoAccess}>
                <MenuItem
                  key="account-menu__sso"
                  className="account-menu__item"
                  onClick={() => {
                    close();
                    dispatch(
                      changeAnalyticsState({
                        page: analytics.page,
                        ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.GLOBAL_SINGLE_SIGNON},
                      })
                    );
                    props.history.push(UserRouteConstants.SSO_ACCOUNTS);
                  }}
                >
                  Single Sign-On
                </MenuItem>
              </RequireScope>
              {props.claims.userTypes &&
                props.claims.userTypes.map(
                  UserTypeClaims.match({
                    [UserTypeClaimKey.AgentSite]: agentClaims =>
                      agentClaims.agencyAssociations.length > 0 && (
                        <MenuItem
                          key={`${agentClaims.firstName} ${agentClaims.recruiterCode}`}
                          className="account-menu__item"
                          onClick={() => {
                            close();
                            dispatch(
                              changeAnalyticsState({
                                page: analytics.page,
                                ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.GLOBAL_SWITCH_ACCOUNTS},
                              })
                            );
                            props.showModal(
                              ModalState.SwitchTaxId({agencyAssociations: agentClaims.agencyAssociations})
                            );
                          }}
                        >
                          Switch Accounts
                        </MenuItem>
                      ),
                    default: () => null,
                  })
                )}
              {props.claims.isImpersonating &&
                props.claims.userTypes &&
                props.claims.userTypes.map(
                  UserTypeClaims.match({
                    [UserTypeClaimKey.AgentSite]: agentClaims =>
                      agentClaims.agencyAssociations.length > 0 && (
                        <MenuItem
                          key="agent-list-menu-item"
                          id="agent-list-menu-item"
                          className="account-menu__item"
                          onClick={() => {
                            close();
                            if (props.claims.originalUserName) {
                              props.stopImpersonation(props.claims.originalUserName);
                            }
                          }}
                        >
                          Return To Agent List
                        </MenuItem>
                      ),
                    default: () => null,
                  })
                )}
              {props.claims.isImpersonating ? (
                <MenuItem
                  id="customer-support-list-menu-item"
                  key="customer-support-list-menu-item"
                  className="account-menu__item"
                  onClick={() => {
                    close();
                    if (props.claims.originalUserName) {
                      dispatch(
                        CustomerSupportActions.stopCustomerSupportImpersonation(props.claims.originalUserName, dispatch)
                      );
                    }
                  }}
                >
                  Return To Customer Support List
                  {setTranslatey('-25%')}
                </MenuItem>
              ) : (
                <MenuItem
                  id="logout-menu-item"
                  key="logout-menu-item"
                  className="account-menu__item"
                  onClick={() => {
                    close();
                    dispatch(logoutISAM());
                    dispatch(toggleLoginSpinner(false));
                  }}
                >
                  Logout
                  {setTranslatey('-45%')}
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </MenuStateProvider>
    </div>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  selectedAgency: state.user.profile.selectedAgency,
  userAlerts: state.alerts.userAlerts,
});

export default connect(mapStateToProps, {
  ...AgencyAdministrationActions,
  showModal,
  setSelectedAgency,
})(withUserClaims(withRouter(AccountMenu)));
